/**
 * PHARMAKON — Fournisseur ERP · the portal's keyboard layer (refonte WP-36; design/ux/01 §4, ux/03 §5.8).
 *
 * The pharmacy shell's KeyboardLayer is not mounted in the portal (its palette and menu keys are
 * built on the pharmacy's NAV_TREE). The portal pages that carry a registry map (PORTAIL_RECL) still
 * need the ONE capture-phase dispatcher and the F1 sheet, so this layer installs exactly those —
 * the same dispatcher, the same ShortcutSheet, no second mechanism:
 *   · F1 toggles « Raccourcis clavier — {page} » (the screen's bound keys + the global F1 / Échap);
 *   · Échap with no overlay clears a focused search field (ux/01 §4.1);
 *   · navigating closes the sheet. Ctrl+K and Alt+M stay unbound in the portal (no handler: the
 *     dispatcher leaves those keys to the page).
 */
import * as React from "react";
import { useLocation } from "react-router-dom";
import {
  installShortcutDispatcher,
  isModalOverlayOpen,
  shortcutRegistry,
  useShortcuts,
  useShortcutRegistryVersion,
} from "@/hooks/useShortcuts.js";
import { useKeyboardStore } from "@/stores/keyboardStore.js";
import { ShortcutSheet } from "@/components/keyboard/ShortcutSheet";
import { clearFocusedSearch } from "@/components/keyboard/KeyboardLayer.tsx";

export default function PortalKeyboardLayer() {
  const helpOpen = useKeyboardStore((s) => s.helpOpen);
  const toggleHelp = useKeyboardStore((s) => s.toggleHelp);
  const closeHelp = useKeyboardStore((s) => s.closeHelp);
  const location = useLocation();

  React.useEffect(() => installShortcutDispatcher(), []);

  const modalAtOpen = React.useRef(false);
  useShortcuts("GLOBAL", {
    AIDE: () => {
      if (!useKeyboardStore.getState().helpOpen) modalAtOpen.current = isModalOverlayOpen();
      toggleHelp();
    },
    FERMER: (e) => clearFocusedSearch(e),
  });

  React.useEffect(() => {
    closeHelp();
  }, [location.pathname, closeHelp]);

  const version = useShortcutRegistryVersion();
  const [sheetData, setSheetData] = React.useState(() => shortcutRegistry.snapshot());
  const [pageTitle, setPageTitle] = React.useState(null);
  React.useEffect(() => {
    if (!helpOpen) return;
    setSheetData(shortcutRegistry.snapshot({ modal: modalAtOpen.current }));
    setPageTitle(document.querySelector("#fs-main h1")?.textContent?.trim() || null);
  }, [helpOpen, version]);

  return <ShortcutSheet open={helpOpen} onClose={closeHelp} data={sheetData} pageTitle={pageTitle} />;
}
